import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'

// Components
import NavigationBar, {
  StyledNavigationBar,
  AnimationWrapper,
} from './components/NavigationBar'
import { Icon, IconWrapper } from './components/icon'
import SecondaryNavigation, {
  ISecondaryNavProps,
} from './components/SecondaryNavigation'
import BackgroundWave from './components/layout/BackgroundWave'
import ReleaseNotesPopup from './components/releaseNotes/ReleaseNotesPopup'

// Styles
import { mqFrom, mqTo } from './styles/mediaQueries'
import { gridConfig } from './styles/responsive'

// Utils
import { useAppState } from './context/AppContext'

interface IProps {
  className?: string
  children: React.ReactNode
  navbarHidden?: boolean
  secondaryNav?: ISecondaryNavProps
}

const AppLayout = (props: IProps) => {
  const appState = useAppState()
  const { theme } = appState

  const [mobileNavbarOpen, setMobileNavbarOpen] = useState(false)
  const [navHeight, setNavHeight] = useState<number>()

  useEffect(() => {
    const sn = props.secondaryNav
    // In-flow navs (mainPage, or a tabbed subPage whose header scrolls away) don't need
    // measured padding — only the fixed navs (tab-less subPage / form) push content down.
    if (
      !sn ||
      sn.version === 'mainPage' ||
      (sn.version === 'subPage' && !!sn.tabs)
    ) {
      // In-flow nav: clear any height measured by a previous (fixed) nav so the
      // reused AppLayout instance doesn't keep stale padding above the content.
      setNavHeight(undefined)
      return
    }
    const nav: HTMLDivElement | null = document?.getElementById(
      'secondaryNav'
    ) as HTMLDivElement
    const height = nav?.getBoundingClientRect().height
    if (nav && height) setNavHeight(height)
  }, [props.secondaryNav])

  return (
    <StyledAppLayout
      className={props.className}
      mobileNavbarOpen={mobileNavbarOpen}
    >
      {!props.navbarHidden && <NavigationBar />}
      {!props.navbarHidden && <ReleaseNotesPopup />}
      <AppPageOuterWrapper withoutNav={props.navbarHidden}>
        {props.navbarHidden && (
          <StyledBackgroundWave color={theme.colors.textLightest} />
        )}
        {props.secondaryNav && (
          <StyledSecondaryNavigation
            {...props.secondaryNav}
            version={props.secondaryNav.version}
            navBarHidden={props.navbarHidden}
          />
        )}
        {!props.navbarHidden && (
          <NavigationBarMobileTrigger
            onClick={() => setMobileNavbarOpen(!mobileNavbarOpen)}
          >
            <Icon
              type={mobileNavbarOpen ? 'Close' : 'Burger'}
              size={24}
              color={theme.colors.lightest}
            />
          </NavigationBarMobileTrigger>
        )}
        <AppPageWrapper
          id="intoView"
          secondaryNavHeight={navHeight}
          version={props.secondaryNav?.version}
        >
          {props.children}
        </AppPageWrapper>
      </AppPageOuterWrapper>
    </StyledAppLayout>
  )
}

const StyledAppLayout = styled.div<{ mobileNavbarOpen: boolean }>`
  display: flex;
  background-color: ${({ theme }) => theme.colors.primaryLight};

  ${mqTo.M`
    ${AnimationWrapper} {
      transition: transform 0.3s ease;
      transform: translateY(0);
    }
  `}

  ${({ mobileNavbarOpen }) =>
    !mobileNavbarOpen
      ? css`
          ${mqTo.M`
            ${AnimationWrapper} {
              transform: translateY(-100%);
            }
          `}

          ${mqTo.M`
          ${StyledNavigationBar} {
            height: 56px;

            > div > div > div {
              pointer-events: none;
            }
          }
        `}
        `
      : css`
          ${mqTo.M`
      height: 100vh;

      ${StyledNavigationBar} {
        height: 100%;
      }
      `}
        `}
`

const formVersionWidth = css<{ theme: any }>`
  max-width: ${(100 / gridConfig.maxColumns) * 10}% !important;
  flex-basis: ${(100 / gridConfig.maxColumns) * 10}%;

  @media (min-width: 1540px) {
    max-width: 1184px !important;
  }
`

const AppPageOuterWrapper = styled.div<{
  withoutNav?: boolean
}>`
  position: relative;
  width: 100%;
  height: 100%;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: ${({ withoutNav }) => (withoutNav ? 0 : 56)}px;
  ${({ withoutNav }) =>
    withoutNav &&
    `
    z-index: 0;
    `}

  ${mqFrom.M`
    padding-left: 48px;
    padding-right: 48px;
  `}

  ${mqFrom.L`
    padding-left: 64px;
    padding-right: 64px;
  `}
`

const StyledBackgroundWave = styled(BackgroundWave)`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: auto;
  /* A narrow screen would leave a strip that hides behind a bottom bar */
  min-height: 45vh;
  z-index: -1;
`

const StyledSecondaryNavigation = styled(SecondaryNavigation)<{
  navBarHidden?: boolean
  version?: 'mainPage' | 'subPage' | 'form'
}>`
  ${({ navBarHidden }) =>
    navBarHidden &&
    `
    top: 0px;
  `}

  ${mqFrom.L`
    > div:first-of-type {
      ${({ version }) => version && version === 'form' && formVersionWidth}
    }
  `}
`

export const AppPageWrapper = styled.div<{
  secondaryNavHeight?: number
  version?: 'mainPage' | 'subPage' | 'form'
}>`
  flex: 1;
  min-height: 100vh;
  padding-bottom: 64px;
  padding-top: ${({ secondaryNavHeight }) =>
    secondaryNavHeight ? secondaryNavHeight + 24 : 24}px;

  ${mqFrom.L`
    ${({ version }) => version && version === 'form' && formVersionWidth}
  `}

  ${mqFrom.M`
    padding-top: 40px;
  `}

  ${mqFrom.L`
    margin-left: auto;
    margin-right: auto;
    max-width: 1184px;
    padding-bottom: 80px;
  `}

  ${({ secondaryNavHeight }) =>
    secondaryNavHeight &&
    css`
      ${mqFrom.M`
        padding-top: ${secondaryNavHeight + 24}px;
      `}
    `}
`

const NavigationBarMobileTrigger = styled.button`
  position: fixed;
  top: 16px;
  right: 20px;
  z-index: 1010;
  display: none;
  padding: 0;
  border: none;
  outline: none;
  background-color: transparent;
  cursor: pointer;

  ${IconWrapper} {
    margin: 0;
  }

  ${mqTo.M`
    display: flex;
  `}
`

export default AppLayout
